import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Paper,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';

export default function TreatmentList() {
  const [treatments, setTreatments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchTreatments();
  }, []);

  const fetchTreatments = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${wpApiSettings.root}dce/v1/treatments`, {
        headers: { 'X-WP-Nonce': wpApiSettings.nonce },
      });
      const data = await response.json();
      setTreatments(data);
    } catch (e) {
      setError('Failed to fetch treatments');
    }
    setLoading(false);
  };

  const handlePriceChange = (id, value) => {
    const newPrice = value.replace(/[^0-9.]/g, ''); // Allow only numbers and dot
    setTreatments((prev) =>
      prev.map((t) => (t.id === id ? { ...t, price: newPrice } : t))
    );
  };

  const savePrice = async (id, price) => {
    if (price === '' || isNaN(price) || Number(price) <= 0) {
      setError('Please enter a valid positive price');
      return;
    }
    setSavingId(id);
    setError('');
    setSuccess('');
    try {
      const response = await fetch(
        `${wpApiSettings.root}dce/v1/treatments/${id}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'X-WP-Nonce': wpApiSettings.nonce,
          },
          body: JSON.stringify({ price: Number(price) }),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to save');
      }
      setSuccess('Price updated successfully');
    } catch (e) {
      setError('Failed to update price');
    }
    setSavingId(null);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Treatment Pricing
      </Typography>

      {loading ? (
        <CircularProgress />
      ) : (
        <Paper sx={{ mt: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Treatment</TableCell>
                <TableCell>Price</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {treatments.map((t) => (
                <TableRow key={t.id}>
                  <TableCell>{t.label}</TableCell>
                  <TableCell sx={{ maxWidth: 120 }}>
                    <TextField
                      variant="outlined"
                      size="small"
                      value={t.price}
                      onChange={(e) => handlePriceChange(t.id, e.target.value)}
                      type="text"
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="contained"
                      size="small"
                      onClick={() => savePrice(t.id, t.price)}
                      disabled={savingId === t.id}
                    >
                      {savingId === t.id ? 'Saving...' : 'Save'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
      >
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess('')}
      >
        <Alert severity="success" onClose={() => setSuccess('')}>
          {success}
        </Alert>
      </Snackbar>
    </Box>
  );
}
